import { BlobURLManager } from "./BlobURLManager";
export class ChunkManager {
    constructor(maxChunks = 100, maxChunkSize = 50 * 1024 * 1024) {
        this.chunks = new Map();
        this.blobURLManager = new BlobURLManager();
        this.maxChunks = maxChunks;
        this.maxChunkSize = maxChunkSize;
    }
    addChunk(blob) {
        // Check chunk size limit
        if (blob.size > this.maxChunkSize) {
            console.warn(`Chunk size ${blob.size} exceeds limit ${this.maxChunkSize}`);
            return "";
        }
        // Check if we need to cleanup
        if (this.chunks.size >= this.maxChunks * 0.8) {
            this.cleanupOldChunks();
        }
        const chunkId = `chunk_${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}`;
        const chunkMetadata = {
            id: chunkId,
            blob,
            size: blob.size,
            timestamp: Date.now(),
            uploadStatus: "pending",
            retryCount: 0,
        };
        this.chunks.set(chunkId, chunkMetadata);
        return chunkId;
    }
    getChunk(chunkId) {
        const chunk = this.chunks.get(chunkId);
        return chunk ? chunk.blob : null;
    }
    getChunkMetadata(chunkId) {
        return this.chunks.get(chunkId) || null;
    }
    markChunkUploading(chunkId) {
        const chunk = this.chunks.get(chunkId);
        if (chunk) {
            chunk.uploadStatus = "uploading";
        }
    }
    markChunkUploaded(chunkId) {
        const chunk = this.chunks.get(chunkId);
        if (chunk) {
            chunk.uploadStatus = "completed";
        }
    }
    markChunkFailed(chunkId) {
        const chunk = this.chunks.get(chunkId);
        if (chunk) {
            chunk.uploadStatus = "failed";
            chunk.retryCount++;
        }
    }
    cleanupChunk(chunkId) {
        const chunk = this.chunks.get(chunkId);
        if (chunk) {
            this.chunks.delete(chunkId);
        }
    }
    cleanupUploadedChunks() {
        const chunksToRemove = [];
        Array.from(this.chunks.entries()).forEach(([chunkId, chunk]) => {
            if (chunk.uploadStatus === "completed") {
                chunksToRemove.push(chunkId);
            }
        });
        chunksToRemove.forEach((chunkId) => this.cleanupChunk(chunkId));
    }
    cleanupOldChunks() {
        const now = Date.now();
        const retentionTime = 5 * 60 * 1000; // 5 minutes
        const chunksToRemove = [];
        Array.from(this.chunks.entries()).forEach(([chunkId, chunk]) => {
            if (now - chunk.timestamp > retentionTime) {
                chunksToRemove.push(chunkId);
            }
        });
        chunksToRemove.forEach((chunkId) => this.cleanupChunk(chunkId));
    }
    cleanupAllChunks() {
        this.chunks.clear();
        this.blobURLManager.revokeAllURLs();
    }
    getStats() {
        const uploadedChunks = Array.from(this.chunks.values()).filter((chunk) => chunk.uploadStatus === "completed").length;
        return {
            totalChunks: this.chunks.size,
            uploadedChunks,
            activeURLs: this.blobURLManager.getActiveURLCount(),
            memoryUsage: this.getMemoryUsage(),
        };
    }
    getPendingChunks() {
        return Array.from(this.chunks.entries())
            .filter(([_, chunk]) => chunk.uploadStatus === "pending")
            .map(([chunkId, _]) => chunkId);
    }
    getFailedChunks() {
        return Array.from(this.chunks.entries())
            .filter(([_, chunk]) => chunk.uploadStatus === "failed")
            .map(([chunkId, _]) => chunkId);
    }
    getMemoryUsage() {
        if ("memory" in performance) {
            return performance.memory.usedJSHeapSize;
        }
        return 0;
    }
}
